// src/utils/permissionsSettingsCache.js

import api from "../axiosConfig";

import {
  getStoredToken,
} from "../lib/authStorage";

export const PERMISSIONS_CACHE_UPDATED_EVENT =
  "loadevo:permissions-cache-updated";

export const PERMISSIONS_ROLE_KEYS = [
  "admin",
  "manager",
  "dispatcher",
  "accountant",
  "driver",
];

export const PERMISSION_KEYS = [
  "loads",
  "vehicles",
  "clients",
  "drivers",
  "places",
  "invoices",
  "notifications",

  "generalSettings",
  "companySettings",
  "profileSettings",
  "usersSettings",
  "permissionsSettings",
  "plansSubscriptionSettings",
  "invoicesStatementsSettings",

  /* =======================================================
   * PERSONNEL — CALENDAR
   * ===================================================== */

  "personnelCalendarView",
  "personnelWorkDaysManage",

  /* =======================================================
   * PERSONNEL — REQUESTS
   * ===================================================== */

  "personnelRequestsView",
  "personnelRequestsCreate",
  "personnelRequestsEdit",
  "personnelRequestsApprove",
  "personnelRequestsCancel",
  "personnelRequestsDelete",

  /* =======================================================
   * PERSONNEL — PUBLIC HOLIDAYS
   * ===================================================== */

  "personnelPublicHolidaysView",
  "personnelPublicHolidaysCreate",
  "personnelPublicHolidaysEdit",
  "personnelPublicHolidaysDelete",

  /* =======================================================
   * PERSONNEL — COMPENSATION
   * ===================================================== */

  "personnelCompensationView",
  "personnelCompensationCreate",
  "personnelCompensationEdit",
  "personnelCompensationConfirm",
  "personnelCompensationDelete",
];

const CACHE_VERSION =
  "v3";

const LEGACY_MATRIX_KEY =
  "permissions.matrix.v1";

const CACHE_MAX_AGE =
  180 *
  24 *
  60 *
  60 *
  1000;

const FRESH_FOR =
  60 *
  1000;

let hotCacheKey =
  null;

let hotCacheEnvelope =
  null;

let inFlightPromise =
  null;

let requestSequence =
  0;

let latestRequestId =
  0;

let localPatchRevision =
  0;

/* -------------------------------------------------------------------------- */
/* Helpers                                                                    */
/* -------------------------------------------------------------------------- */

const safeParse =
  (
    value
  ) => {
    try {
      return JSON.parse(
        value
      );
    } catch {
      return null;
    }
  };

const hashString =
  (
    value
  ) => {
    let hash =
      2166136261;

    const source =
      String(
        value ||
          ""
      );

    for (
      let index = 0;
      index <
      source.length;
      index += 1
    ) {
      hash ^=
        source.charCodeAt(
          index
        );

      hash =
        Math.imul(
          hash,
          16777619
        );
    }

    return (
      hash >>>
      0
    ).toString(
      36
    );
  };

const resolveScope =
  () => {
    const token =
      getStoredToken();

    return token
      ? `token-${hashString(
          token
        )}`
      : "anonymous";
  };

const buildCacheKey =
  () =>
    `loadevo.permissions-settings.${resolveScope()}.${CACHE_VERSION}`;

/* -------------------------------------------------------------------------- */
/* Normalization                                                              */
/* -------------------------------------------------------------------------- */

export const normalizePermissionsMatrix =
  (
    matrix
  ) => {
    const output = {};

    PERMISSIONS_ROLE_KEYS.forEach(
      (
        role
      ) => {
        output[
          role
        ] = {};

        PERMISSION_KEYS.forEach(
          (
            key
          ) => {
            output[
              role
            ][
              key
            ] =
              Boolean(
                matrix?.[
                  role
                ]?.[
                  key
                ]
              );
          }
        );
      }
    );

    return output;
  };

export const mergePermissionsMatrix =
  (
    currentMatrix,
    incomingMatrix
  ) => {
    const current =
      normalizePermissionsMatrix(
        currentMatrix
      );

    const merged = {
      ...current,
    };

    PERMISSIONS_ROLE_KEYS.forEach(
      (
        role
      ) => {
        if (
          !incomingMatrix?.[
            role
          ]
        ) {
          return;
        }

        merged[
          role
        ] = {
          ...current[
            role
          ],

          ...incomingMatrix[
            role
          ],
        };
      }
    );

    return normalizePermissionsMatrix(
      merged
    );
  };

/* -------------------------------------------------------------------------- */
/* Notifications                                                              */
/* -------------------------------------------------------------------------- */

const emitCacheUpdate =
  (
    data
  ) => {
    if (
      typeof window ===
      "undefined"
    ) {
      return;
    }

    window.dispatchEvent(
      new CustomEvent(
        PERMISSIONS_CACHE_UPDATED_EVENT,
        {
          detail:
            data,
        }
      )
    );

    /*
     * Keep compatibility with Sidebar / permission consumers
     * already listening to this old event.
     */
    window.dispatchEvent(
      new Event(
        "permissions:changed"
      )
    );
  };

/* -------------------------------------------------------------------------- */
/* Cache read                                                                 */
/* -------------------------------------------------------------------------- */

export const readPermissionsSettingsCache =
  () => {
    if (
      typeof window ===
      "undefined"
    ) {
      return null;
    }

    const cacheKey =
      buildCacheKey();

    if (
      hotCacheKey ===
        cacheKey &&
      hotCacheEnvelope?.data
    ) {
      if (
        Date.now() -
          hotCacheEnvelope.savedAt <=
        CACHE_MAX_AGE
      ) {
        return hotCacheEnvelope.data;
      }

      hotCacheKey =
        null;

      hotCacheEnvelope =
        null;
    }

    try {
      const raw =
        window.localStorage.getItem(
          cacheKey
        );

      if (raw) {
        const parsed =
          safeParse(
            raw
          );

        if (
          parsed?.data?.matrix &&
          typeof parsed.savedAt ===
            "number" &&
          Date.now() -
            parsed.savedAt <=
            CACHE_MAX_AGE
        ) {
          hotCacheKey =
            cacheKey;

          hotCacheEnvelope =
            parsed;

          return parsed.data;
        }

        window.localStorage.removeItem(
          cacheKey
        );
      }

      /*
       * Migration / compatibility.
       *
       * Old data may not contain the new Personnel permissions,
       * therefore fetchedAt stays at 0 so the authoritative
       * backend matrix is refreshed immediately.
       */
      const legacy =
        safeParse(
          window.localStorage.getItem(
            LEGACY_MATRIX_KEY
          )
        );

      if (
        legacy &&
        typeof legacy ===
          "object" &&
        Object.keys(
          legacy
        ).length >
          0
      ) {
        const migrated = {
          matrix:
            normalizePermissionsMatrix(
              legacy
            ),

          fetchedAt:
            0,
        };

        writePermissionsSettingsCache(
          migrated.matrix,
          {
            fetchedAt:
              0,
          }
        );

        return migrated;
      }

      return null;
    } catch (
      error
    ) {
      console.warn(
        "[permissionsSettingsCache] read failed:",
        error
      );

      return null;
    }
  };

/* -------------------------------------------------------------------------- */
/* Cache write                                                                */
/* -------------------------------------------------------------------------- */

export const writePermissionsSettingsCache =
  (
    matrix,
    {
      fetchedAt =
        Date.now(),
    } = {}
  ) => {
    const normalizedMatrix =
      normalizePermissionsMatrix(
        matrix
      );

    const nextData = {
      matrix:
        normalizedMatrix,

      fetchedAt,
    };

    if (
      typeof window ===
      "undefined"
    ) {
      return nextData;
    }

    const cacheKey =
      buildCacheKey();

    const envelope = {
      savedAt:
        Date.now(),

      data:
        nextData,
    };

    /*
     * Memory first.
     */
    hotCacheKey =
      cacheKey;

    hotCacheEnvelope =
      envelope;

    try {
      window.localStorage.setItem(
        cacheKey,
        JSON.stringify(
          envelope
        )
      );

      /*
       * Preserve the old raw matrix cache because other parts
       * of LOADEvo may already read this exact key.
       */
      window.localStorage.setItem(
        LEGACY_MATRIX_KEY,
        JSON.stringify(
          normalizedMatrix
        )
      );
    } catch (
      error
    ) {
      console.warn(
        "[permissionsSettingsCache] write failed:",
        error
      );
    }

    emitCacheUpdate(
      nextData
    );

    return nextData;
  };

/* -------------------------------------------------------------------------- */
/* Optimistic role patch                                                      */
/* -------------------------------------------------------------------------- */

export const patchPermissionsRoleCache =
  (
    role,
    updater
  ) => {
    if (
      !PERMISSIONS_ROLE_KEYS.includes(
        role
      )
    ) {
      return (
        readPermissionsSettingsCache()
      );
    }

    localPatchRevision +=
      1;

    const current =
      readPermissionsSettingsCache();

    const currentMatrix =
      current?.matrix ||
      normalizePermissionsMatrix(
        {}
      );

    const currentRole =
      currentMatrix[
        role
      ] ||
      {};

    const nextRole =
      typeof updater ===
      "function"
        ? updater(
            currentRole
          )
        : updater;

    const nextMatrix = {
      ...currentMatrix,

      [
        role
      ]: {
        ...currentRole,
        ...(nextRole ||
          {}),
      },
    };

    return writePermissionsSettingsCache(
      nextMatrix,
      {
        /*
         * An optimistic patch is NOT a successful server refresh.
         */
        fetchedAt:
          current?.fetchedAt ||
          0,
      }
    );
  };

/* -------------------------------------------------------------------------- */
/* Subscription                                                               */
/* -------------------------------------------------------------------------- */

export const subscribePermissionsSettingsCache =
  (
    callback
  ) => {
    if (
      typeof window ===
        "undefined" ||
      typeof callback !==
        "function"
    ) {
      return () => {};
    }

    const cacheKey =
      buildCacheKey();

    const customHandler =
      (
        event
      ) => {
        callback(
          event?.detail ??
            readPermissionsSettingsCache()
        );
      };

    const storageHandler =
      (
        event
      ) => {
        if (
          event.key !==
          cacheKey
        ) {
          return;
        }

        hotCacheKey =
          null;

        hotCacheEnvelope =
          null;

        callback(
          readPermissionsSettingsCache()
        );
      };

    window.addEventListener(
      PERMISSIONS_CACHE_UPDATED_EVENT,
      customHandler
    );

    window.addEventListener(
      "storage",
      storageHandler
    );

    return () => {
      window.removeEventListener(
        PERMISSIONS_CACHE_UPDATED_EVENT,
        customHandler
      );

      window.removeEventListener(
        "storage",
        storageHandler
      );
    };
  };

/* -------------------------------------------------------------------------- */
/* Network                                                                    */
/* -------------------------------------------------------------------------- */

const requestConfig =
  (
    force
  ) => {
    if (!force) {
      return {
        headers: {
          "Cache-Control":
            "no-cache",
        },
      };
    }

    return {
      params: {
        _permissionsRefresh:
          Date.now(),
      },

      headers: {
        "Cache-Control":
          "no-store",

        Pragma:
          "no-cache",
      },
    };
  };

export const prefetchPermissionsSettingsData =
  async ({
    force = false,
  } = {}) => {
    const cached =
      readPermissionsSettingsCache();

    if (
      !force &&
      cached?.fetchedAt &&
      Date.now() -
        cached.fetchedAt <
        FRESH_FOR
    ) {
      return cached;
    }

    if (
      !force &&
      inFlightPromise
    ) {
      return inFlightPromise;
    }

    const requestId =
      ++requestSequence;

    latestRequestId =
      requestId;

    const patchRevisionAtStart =
      localPatchRevision;

    const promise =
      (async () => {
        try {
          const {
            data,
          } =
            await api.get(
              "/permissions/matrix",
              requestConfig(
                force
              )
            );

          if (
            requestId !==
              latestRequestId ||
            patchRevisionAtStart !==
              localPatchRevision
          ) {
            return (
              readPermissionsSettingsCache() ||
              cached ||
              null
            );
          }

          if (
            !data?.matrix
          ) {
            return (
              readPermissionsSettingsCache() ||
              cached ||
              null
            );
          }

          return writePermissionsSettingsCache(
            data.matrix,
            {
              fetchedAt:
                Date.now(),
            }
          );
        } catch (
          error
        ) {
          if (cached) {
            return cached;
          }

          throw error;
        }
      })();

    inFlightPromise =
      promise;

    try {
      return await promise;
    } finally {
      if (
        inFlightPromise ===
        promise
      ) {
        inFlightPromise =
          null;
      }
    }
  };